import { useState, useMemo, useCallback, useRef, useEffect, createElement } from 'react';
import { Link, NavLink, Outlet, useLocation, useMatch, useMatches, useNavigate } from 'react-router';
import { RouteErrorBoundary } from '@/components/common/RouteErrorBoundary';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useProductStore } from '@/stores/product';
import { useThemeStore } from '@/stores/theme';
import { getBrandMeta } from '@/lib/brand-theme';
import { resolveIcon } from '@/lib/icons';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { DensityToggle } from '@/components/layout/DensityToggle';
import { EnvironmentSelector } from '@/components/layout/EnvironmentSelector';
import { DashboardSelector } from '@/components/layout/DashboardSelector';
import { TourOverlay } from '@/components/tour/TourOverlay';
import { TourLauncher } from '@/components/tour/TourLauncher';
import { useTourStore } from '@/stores/tour';
import { getFilteredSteps } from '@/lib/tour/registry';
import {
  Rocket,
  BookOpen,
  Database,
  Workflow,
  Layers,
  BarChart3,
  BookOpenCheck,
  GitBranch,
  BarChart2,
  Server,
  Calendar,
  Settings,
  Users,
  Key,
  Bell,
  User,
  LogOut,
  ChevronDown,
  AlertTriangle,
  ShieldCheck,
  PanelLeft,
  PanelLeftClose,
  Compass,
  LayoutGrid,
  Sprout,
  Table2,
  Shield,
  Menu,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import './PortalLayout.css';

// Page titles / descriptions keyed by path
const pageTitles: Record<string, string> = {
  '/setup': 'Setup Guide',
  '/catalog': 'Catalog',
  '/queries': 'Data Explorer',
  '/notebooks': 'Notebooks',
  '/seed-data': 'Seed Data',
  '/coordinators': 'Agents',
  '/jobs': 'Jobs',
  '/data-sources': 'Data Sources',
  '/data-sources/new': 'Add Data Source',
  '/pipelines': 'Pipelines',
  '/pipelines/new': 'New Pipeline',
  '/transforms': 'Transforms',
  '/transforms/new': 'New Transform',
  '/bi': 'Dashboards',
  '/ai': 'Scout',
  '/settings/account': 'Account',
  '/settings': 'App Settings',
  '/settings/organization': 'Organization',
  '/settings/environments': 'Environments',
  '/settings/api-keys': 'API Keys',
  '/settings/policies': 'Data Policies',
  '/settings/notifications': 'Notifications',
  '/settings/security-groups': 'Security Groups',
  '/settings/admin': 'Admin Panel',
  '/admin/permissions': 'Permission Editor',
};

const pageDescriptions: Record<string, string> = {
  '/setup': 'Configure your data lake and start ingesting data',
  '/catalog': 'Browse namespaces, tables, and schemas in your data lake',
  '/queries': 'Explore, query, and visualize your data',
  '/notebooks': 'Write and execute Solidafy DSL in interactive cells',
  '/seed-data': 'Manage enrichment data like team assignments, display names, and identity mappings',
  '/coordinators': 'Monitor agents that run sync jobs',
  '/jobs': 'View and manage sync jobs',
  '/pipelines': 'Build and manage your data pipelines',
  '/transforms': 'Build visual DSL pipelines for data transformations',
  '/bi': 'Create and view analytics dashboards',
  '/ai': 'Scout your data using natural language',
  '/data-sources': 'Manage data sources for ingestion into Solidafy',
  '/settings/account': 'Manage your profile, password, and email',
  '/settings': 'Customize theme, display density, and preferences',
  '/settings/organization': 'Manage your organization settings',
  '/settings/environments': 'Manage environments (dev, staging, production)',
  '/settings/api-keys': 'Generate API keys for agents and CLI access',
  '/settings/policies': 'Manage row-level access control policies for data tables',
  '/settings/notifications': 'Configure where alerts are sent: email, Slack, or in-app',
  '/settings/security-groups': 'Manage user access to specific resources via security groups',
  '/settings/admin': 'Danger zone - destructive operations',
  '/admin/permissions': 'Edit role access for each platform feature',
};

// Routes where we hide the default layout header (page provides its own)
const hiddenHeaderPatterns = [
  /^\/pipelines\/new$/,
  /^\/pipelines\/.+\/edit$/,
  /^\/queries$/,
  /^\/bi\/.+/,
  /^\/data-sources\/new$/,
  /^\/data-sources\/[^/]+$/,
  /^\/notebooks\/[^/]+$/,
  /^\/transforms\/new$/,
  /^\/transforms\/[^/]+$/,
  /^\/ai$/,
  /^\/p\/[^/]+\/providers$/,
  /^\/p\/[^/]+\/data$/,
];

// Routes where portal-content should have no padding
const noPaddingPatterns = [
  /^\/notebooks\/[^/]+$/,
  /^\/queries$/,
  /^\/ai$/,
  /^\/pipelines\/new$/,
  /^\/pipelines\/[^/]+\/edit$/,
  /^\/transforms\/new$/,
  /^\/transforms\/[^/]+$/,
  /^\/bi\/[^/]+$/,
];

function getPageTitle(path: string): string {
  if (pageTitles[path]) return pageTitles[path];
  // Product providers pages
  const providerMatch = path.match(/^\/p\/([^/]+)\/providers$/);
  if (providerMatch) return 'Manage';
  const dataTablesMatch = path.match(/^\/p\/([^/]+)\/data$/);
  if (dataTablesMatch) return 'Data Tables';
  if (path.startsWith('/data-sources/')) return 'Data Source Details';
  if (path.startsWith('/notebooks/')) return 'Notebook';
  if (path.startsWith('/coordinators/')) return 'Agent Details';
  if (path.startsWith('/jobs/')) return 'Job Details';
  return 'Dashboard';
}

function getPageDescription(path: string): string {
  if (pageDescriptions[path]) return pageDescriptions[path];
  const providerMatch = path.match(/^\/p\/([^/]+)\/providers$/);
  if (providerMatch) return 'Manage your product connectors, schedules, and settings';
  const dataTablesDescMatch = path.match(/^\/p\/([^/]+)\/data$/);
  if (dataTablesDescMatch) return 'Manage reference data, team assignments, and configuration';
  if (path.startsWith('/data-sources/')) return 'View data source configuration and sync history';
  if (path.startsWith('/notebooks/')) return 'Execute Solidafy DSL code in cells';
  if (path.startsWith('/coordinators/')) return 'Agent metrics and job history';
  if (path.startsWith('/jobs/')) return 'Job details and execution history';
  return '';
}

export function PortalLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const matches = useMatches();
  const is404 = matches.some((m) => m.id === 'not-found');
  const { user, logout } = useAuthStore();
  const { branding, productDashboards, fetchProductDashboards, allProducts, loadProducts } = useProductStore();
  const { brand } = useThemeStore();
  const { can, isStaff, role, activeOrg } = usePermissions();
  const { resetTour, startTour: launchTour, activeTour: runningTour } = useTourStore();
  const brandMeta = brand ? getBrandMeta(brand) : null;

  const userEmail = user?.email || 'Loading...';
  const userPlan = useMemo(() => {
    const plan = activeOrg?.plan || 'trial';
    return plan.charAt(0).toUpperCase() + plan.slice(1) + ' Plan';
  }, [activeOrg]);

  const hideLayoutHeader = useMemo(
    () => is404 || hiddenHeaderPatterns.some((p) => p.test(location.pathname)),
    [is404, location.pathname],
  );

  const noPadding = useMemo(
    () => is404 || noPaddingPatterns.some((p) => p.test(location.pathname)),
    [is404, location.pathname],
  );

  const pageTitle = getPageTitle(location.pathname);
  const pageDescription = getPageDescription(location.pathname);

  const handleLogout = useCallback(async () => {
    await logout();
    void navigate('/login');
  }, [logout, navigate]);

  // Fetch dynamic product list from API
  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  // Fetch product dashboards on mount and product change
  useEffect(() => {
    if (branding) {
      void fetchProductDashboards(branding.slug);
    }
  }, [branding, fetchProductDashboards]);

  // Fetch dashboards for viewer in platform mode (no active product)
  useEffect(() => {
    if (!branding && role === 'viewer' && allProducts.length > 0) {
      void fetchProductDashboards(allProducts[0].slug);
    }
  }, [branding, role, fetchProductDashboards, allProducts]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Focus trap for mobile sidebar overlay
  useEffect(() => {
    if (!mobileOpen) return;
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const focusable = sidebar.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    first?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  const sidebarTitle = branding?.name ?? 'Solidafy';

  return (
    <div className="portal-layout">
      {/* Mobile sidebar backdrop */}
      {mobileOpen && <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`portal-sidebar${sidebarCollapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}
        aria-label="Sidebar"
      >
        <div className="sidebar-header">
          <Link to="/" className="logo">
            {brandMeta?.logoIsName && brandMeta.logoUrl ? (
              <div className="logo-text">
                <img src={brandMeta.logoUrl} alt={brandMeta.name} className="logo-wordmark" />
                <span className="logo-powered-by">Powered by Solidafy</span>
              </div>
            ) : brandMeta?.iconUrl ? (
              <>
                <div className="logo-icon logo-icon-brand">
                  <img src={brandMeta.iconUrl} alt={brandMeta.name} className="logo-icon-img" />
                </div>
                <div className="logo-text">
                  <h1 className="logo-name">{brandMeta.name}</h1>
                  <span className="logo-powered-by">Powered by Solidafy</span>
                </div>
              </>
            ) : (
              <>
                <div className="logo-icon">
                  <DynamicIcon iconKey={branding?.icon} fallback={Rocket} size={14} />
                </div>
                <div className="logo-text">
                  <h1 className="logo-name">{sidebarTitle}</h1>
                </div>
              </>
            )}
          </Link>
          <button
            className="collapse-btn"
            onClick={() => setSidebarCollapsed((c) => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? <PanelLeft size={15} /> : <PanelLeftClose size={15} />}
          </button>
          <button className="sidebar-close-btn" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
            <PanelLeftClose size={15} />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Main navigation">
          {branding ? (
            <>
              {/* Product nav */}
              <NavSection label={branding.name}>
                <SidebarLink to="/bi" icon={LayoutGrid} label="Overview" />
                {productDashboards.map((dash) => (
                  <SidebarLink key={dash.id} to={`/bi/${dash.id}`} icon={BarChart2} label={dash.name} end />
                ))}
                <SidebarLink to="/ai" icon={Compass} label="Scout" />
                <SidebarLink to={`/p/${branding.slug}/data`} icon={Table2} label="Data Tables" />
                <SidebarLink to={`/p/${branding.slug}/providers`} icon={Settings} label="Manage" />
              </NavSection>
              <NavSection label="Settings">
                <SidebarLink to="/settings" icon={Settings} label="Settings" end />
              </NavSection>
            </>
          ) : (
            <>
              {/* Get Started */}
              {can('get_started.setup_guide') && (
                <NavSection label="Get Started">
                  <SidebarLink to="/setup" icon={BookOpen} label="Setup Guide" className="setup-guide" />
                </NavSection>
              )}

              {/* Ingestion */}
              {(can('ingestion.data_sources') || can('ingestion.pipelines') || can('ingestion.transforms')) && (
                <NavSection label="Ingestion" dataTour="sidebar-ingestion">
                  {can('ingestion.data_sources') && (
                    <SidebarLink to="/data-sources" icon={Database} label="Data Sources" dataTour="sidebar-datasources" />
                  )}
                  {can('ingestion.pipelines') && <SidebarLink to="/pipelines" icon={Workflow} label="Pipelines" dataTour="sidebar-pipelines" />}
                  {can('ingestion.transforms') && <SidebarLink to="/transforms" icon={GitBranch} label="Transforms" />}
                </NavSection>
              )}

              {/* Data Lake */}
              {(can('data_lake.catalog') ||
                can('data_lake.explorer') ||
                can('data_lake.notebooks') ||
                can('data_lake.seed_data') ||
                can('data_lake.policies')) && (
                <NavSection label="Data Lake">
                  {can('data_lake.catalog') && <SidebarLink to="/catalog" icon={Layers} label="Catalog" dataTour="sidebar-catalog" />}
                  {can('data_lake.explorer') && <SidebarLink to="/queries" icon={BarChart3} label="Data Explorer" dataTour="sidebar-explorer" />}
                  {can('data_lake.notebooks') && <SidebarLink to="/notebooks" icon={BookOpenCheck} label="Notebooks" />}
                  {can('data_lake.seed_data') && <SidebarLink to="/seed-data" icon={Sprout} label="Seed Data" />}
                  {can('data_lake.policies') && (
                    <SidebarLink to="/settings/policies" icon={Shield} label="Data Policies" />
                  )}
                </NavSection>
              )}

              {/* Analytics */}
              {can('analytics.dashboards') && (
                <NavSection label="Analytics">
                  {role === 'viewer' ? (
                    <>
                      <SidebarLink to="/bi" icon={BarChart2} label="Dashboards" end />
                      {productDashboards.map((dash) => (
                        <SidebarLink key={dash.id} to={`/bi/${dash.id}`} icon={BarChart2} label={dash.name} end />
                      ))}
                    </>
                  ) : (
                    <SidebarLink to="/bi" icon={BarChart2} label="Dashboards" dataTour="sidebar-dashboards" />
)}
                </NavSection>
              )}

              {/* Scout */}
              {can('scout.scout') && (
                <NavSection label="Scout">
                  <SidebarLink to="/ai" icon={Compass} label="Scout" />
                </NavSection>
              )}

              {/* Infrastructure */}
              {(can('infrastructure.agents') || can('infrastructure.jobs')) && (
                <NavSection label="Infrastructure">
                  {can('infrastructure.agents') && <SidebarLink to="/coordinators" icon={Server} label="Agents" />}
                  {can('infrastructure.jobs') && <SidebarLink to="/jobs" icon={Calendar} label="Jobs" />}
                </NavSection>
              )}

              {/* Settings */}
              <NavSection label="Settings" dataTour="sidebar-settings">
                <SidebarLink to="/settings/account" icon={User} label="Account" />
                {can('settings.app_settings') && (
                  <SidebarLink to="/settings" icon={Settings} label="App Settings" end />
                )}
                {can('settings.organization') && (
                  <SidebarLink to="/settings/organization" icon={Users} label="Organization" />
                )}
                {can('settings.environments') && (
                  <SidebarLink to="/settings/environments" icon={Server} label="Environments" />
                )}
                {can('settings.api_keys') && <SidebarLink to="/settings/api-keys" icon={Key} label="API Keys" />}
                {can('settings.notifications') && (
                  <SidebarLink to="/settings/notifications" icon={Bell} label="Notifications" />
                )}
                {can('settings.security_groups') && (
                  <SidebarLink to="/settings/security-groups" icon={ShieldCheck} label="Security Groups" />
                )}
                {can('settings.sso') && <SidebarLink to="/settings/sso" icon={Shield} label="SSO" />}
              </NavSection>

              {/* Advanced (collapsible) — shown for owners/admins and Solidafy staff */}
              {(can('advanced.admin_panel') || isStaff) && (
                <div className="nav-section">
                  <div
                    className="nav-section-header nav-section-collapsible"
                    role="button"
                    tabIndex={0}
                    aria-expanded={advancedExpanded}
                    onClick={() => setAdvancedExpanded((e) => !e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setAdvancedExpanded((v) => !v);
                      }
                    }}
                  >
                    <span>Advanced</span>
                    <ChevronDown size={14} className={advancedExpanded ? 'rotated' : ''} />
                  </div>
                  {advancedExpanded && (
                    <div className="nav-section-content">
                      {can('advanced.admin_panel') && (
                        <SidebarLink to="/settings/admin" icon={AlertTriangle} label="Admin" danger />
                      )}
                      {isStaff && <SidebarLink to="/admin/permissions" icon={ShieldCheck} label="Permission Editor" />}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="user-avatar user-avatar-img" />
            ) : (
              <div className="user-avatar">
                <User size={15} />
              </div>
            )}
            <div className="user-info">
              <div className="user-name">{userEmail}</div>
              <div className="user-role">{userPlan}</div>
            </div>
          </div>
          <EnvironmentSelector />
          <div className="footer-actions">
            <ThemeToggle />
            <DensityToggle />
            {!branding && (
              <button
                onClick={() => {
                  resetTour('getting-started');
                  const steps = getFilteredSteps('getting-started', can);
                  if (steps.length > 0) launchTour('getting-started', steps);
                }}
                className="tour-restart-btn"
                title="Take a guided tour"
                disabled={!!runningTour}
              >
                <HelpCircle size={15} />
                <span>Tour</span>
              </button>
            )}
            <button onClick={() => void handleLogout()} className="logout-btn">
              <LogOut size={15} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Guided tour */}
      <TourOverlay />
      <TourLauncher />

      {/* Main content */}
      <div className={`portal-main${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        {/* Mobile top bar — hamburger + logo, hidden on desktop */}
        <div className="mobile-topbar">
          <button
            className="hamburger-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
          >
            <Menu size={20} />
          </button>
          <Link to="/" className="mobile-logo">
            <DynamicIcon iconKey={branding?.icon} fallback={Rocket} size={14} />
            <span>{sidebarTitle}</span>
          </Link>
        </div>

        <main className={`portal-content${noPadding ? ' no-padding' : ''}`} aria-label="Main content">
          {!hideLayoutHeader && (
            <div className="content-header">
              <div className="header-left">
                <h1>{pageTitle}</h1>
                {pageDescription && <p className="page-description">{pageDescription}</p>}
              </div>
              <div className="header-right">
                <DashboardSelector />
                <Link to="/settings" className="header-action" title="App Settings">
                  <Settings size={15} />
                </Link>
              </div>
            </div>
          )}
          <RouteErrorBoundary locationKey={location.pathname}>
            <Outlet key={user?.active_environment_id ?? ''} />
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}

/* -- Helper sub-components -- */

function DynamicIcon({ iconKey, fallback, size }: { iconKey?: string; fallback: LucideIcon; size: number }) {
  // Use createElement to avoid react-hooks/static-components warning with JSX dynamic components
  return createElement(iconKey ? resolveIcon(iconKey, fallback) : fallback, { size });
}

function NavSection({ label, children, dataTour }: { label: string; children: React.ReactNode; dataTour?: string }) {
  return (
    <div className="nav-section" data-tour={dataTour}>
      <div className="nav-section-header">{label}</div>
      {children}
    </div>
  );
}

type SidebarLinkProps = {
  to: string;
  icon: React.ComponentType<{ size: number }>;
  label: string;
  className?: string;
  danger?: boolean;
  end?: boolean;
  dataTour?: string;
};

function SidebarLink({ to, icon: Icon, label, className, danger, end, dataTour }: SidebarLinkProps) {
  const match = useMatch({ path: to, end: end ?? false });
  return (
    <NavLink
      to={to}
      end={end}
      aria-current={match ? 'page' : undefined}
      data-tour={dataTour}
      className={({ isActive }) =>
        `nav-item${isActive ? ' active' : ''}${danger ? ' nav-item-danger' : ''}${className ? ` ${className}` : ''}`
      }
    >
      <Icon size={15} aria-hidden={true} />
      <span>{label}</span>
    </NavLink>
  );
}

